import React, {useEffect} from "react";
import styled from "styled-components";
import {Box} from "@mui/material";
import {Exist, HOCAccordion} from "components";
import {PureButton} from "components/Button";
import {SubLink} from "components/Header/SubLink";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {isMenuOpen} from "modules/selectors";
import {toggleMenu} from "modules/actions";
import {SUB_NAVIGATION_ITEMS, TUTORIAL, ViewPortVisibility} from "modules";
import {ISubNavigationItem} from "modules/types";

import nav from "assets/images/icons/nav_mobile.svg";
import navClose from "assets/images/icons/nav_close.svg";
import openIn from "assets/images/icons/open_in.svg";
import {ChevronLeft} from "@mui/icons-material";
import {MobileLanguageSelector} from "components/Header/MobileLanguageSelector";
import {TutorialLink} from "./TutorialLink";
import {useLocation} from "react-router-dom";

const MenuToggle = styled(PureButton)`
	padding-top: 8px;

	img {
		vertical-align: middle;
	}
`;

const Menu = styled.ul`
	list-style: none;
	padding: 0 0 12px 44px;
	position: fixed;
	top: 56px;
	height: 100%;
	width: 100%;
	left: 0;
	background-color: ${({theme}) => theme.global.header_background};
	transform: translateX(-100%);
	transition: 0.25s;

	&.open {
		transform: unset;
	}

	li {
		margin-bottom: 28px;
	}
`;

const LinkWrapper = styled.div`
	&.spaced {
		padding-top: 90px;
	}

	a {
		padding: 14px 0;
		display: block;
		color: #ffffff;
		font-style: normal;
		font-weight: 500;
		font-size: 14px;
		line-height: 160%;

		&.trivia {
			color: #00b8ff;
		}

		&.brackets {
			color: #e40046;
		}

		&:hover,
		&:active,
		&:focus {
			color: ${({theme}) => theme.global.header_hover_color};
		}
	}
`;

const ExternalLink = styled.a`
	display: flex !important;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding-right: 22px !important;

	span {
		margin-right: auto;
	}

	&.with-icon {
		span,
		svg {
			position: relative;
			left: -12px;
		}
	}

	.coca-cola-logo {
		height: 20px;
		width: auto;
	}
`;

export const HamburgerMenu: React.FC = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const {t} = useTranslation();
	const isOpen = useSelector(isMenuOpen);
	const openClass = isOpen ? "open" : "";

	const toggleMobileMenu = () => {
		dispatch(toggleMenu(!isOpen));
	};

	useEffect(() => {
		dispatch(toggleMenu(false));
	}, [dispatch, location]);

	return (
		<Box
			sx={{
				position: "relative",
			}}>
			<MenuToggle onClick={toggleMobileMenu}>
				{isOpen ? <img src={navClose} alt="Close" /> : <img src={nav} alt="Open" />}
			</MenuToggle>
			<Menu className={openClass}>
				<LinkWrapper>
					<ExternalLink href="/" className="with-icon">
						<ChevronLeft />
						<span>{t("subheader.fifa.fifa_back")}</span>
					</ExternalLink>
				</LinkWrapper>

				{SUB_NAVIGATION_ITEMS.map((item) => (
					<LinkWrapper key={item.name}>
						<SubAccordion item={item} />
					</LinkWrapper>
				))}
				<MobileLanguageSelector />

				<LinkWrapper className="spaced">
					<ExternalLink href="/">
						<span>{t("subheader.fifa.fifa_plus")}</span>
						<img src={openIn} alt="open" />
					</ExternalLink>
					<ExternalLink href="https://www.fifa.com/">
						<span>{t("subheader.fifa.fifa_com")}</span>
						<img src={openIn} alt="open" />
					</ExternalLink>
				</LinkWrapper>
			</Menu>
		</Box>
	);
};

const SubLinkWrapper = styled.div`
	padding-left: 20px;
	> a.active {
		color: ${({theme}) => theme.global.header_hover_color};
	}
`;

interface ISubAccordionProps {
	item: ISubNavigationItem;
}

export const SubAccordion: React.FC<ISubAccordionProps> = ({item}) => {
	const isSubItemsVisible =
		item.subItems?.length !== 0 && item.subItemsVisibility !== ViewPortVisibility.DesktopOnly;
	const isTutorialVisible = Boolean(item.projectName && TUTORIAL[item.projectName]);

	if (item.hidden) {
		return null;
	}

	if (isSubItemsVisible) {
		return (
			<HOCAccordion item={<SubLink item={item} />}>
				{item.subItems?.map((item) => (
					<SubLinkWrapper key={item.name}>
						<SubLink item={item} />
					</SubLinkWrapper>
				))}
				<Exist when={isTutorialVisible}>
					<SubLinkWrapper>
						<TutorialLink />
					</SubLinkWrapper>
				</Exist>
			</HOCAccordion>
		);
	}

	return <SubLink item={item} />;
};
